/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HeadersFunction, MetaFunction, useLoaderData, useSearchParams } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import Chip from "~/components/shared-components/Chip";
import ClientOnly from "~/components/shared-components/ClentOnly";
import DisplayBookCard from "~/components/shared-components/DisplayBookCard";
import NodataFound from "~/components/shared-components/NoDataFound";
import NoHasMoreText from "~/components/shared-components/NoHasMoreText";
import SadGranthFilterData from "~/data/sadGranthFilterData.json";
import useDisplayStickyButton from "~/hook/useDisplayStickyButton";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { BookListType } from "~/interface/sad-granth-types/book-list";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

interface LoaderData {
  bookList: BookListType[];
  filterType?: string;
  title: string;
}

export const meta: MetaFunction<typeof loader> = ({ location, params, data }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";
  const search = location?.search || "";
  const type = new URLSearchParams(search).get("type");

  const meta = getPageMeta(type || ("sadGranth" as any), lang);

  // Calculate the actual count based on type
  const loaderData = data as LoaderData;
  const bookList = loaderData?.bookList || [];
  let totalCount = bookList.length;

  // If there's a filter type, count only matching books
  if (type) {
    totalCount = bookList.filter((book: BookListType) => book.type?.toLowerCase() === type.toLowerCase()).length;
  }

  // Replace {total_count} placeholder with actual count
  const replaceCount = (text: string) => text.replace(/{total_count}/g, totalCount.toString());

  return metaService({
    title: replaceCount(meta.metaTitle),
    description: replaceCount(meta.metaDescription),
    ogTitle: replaceCount(meta.ogTitle),
    ogDescription: replaceCount(meta.ogDescription),
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname + search,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}${search}` },
      { hrefLang: "en", href: `${pathname}${search}` },
      { hrefLang: "gu", href: `${pathname}${search}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string }; request: Request }): Promise<any> => {
  const { lang } = params;
  const apiService = new ApiService(lang || LANG.english);
  const filterType = new URL(request.url).searchParams.get("type") || null;
  const response = await apiService.getSadGranth();

  const DATA = {
    bookList: response,
    title: lang === LANG.english ? "Sad-Granth" : "સદ્ગ્રંથ",
    filterType,
  };

  const etag = computeEtagFromData(DATA);

  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: {
      ...CACHED_PAGE.SAD_GRANTH.HEADERS,
      ETag: etag,
    },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || "Sad Granth", path: "/sad-granth" },
  ],
};

const SadGranth = () => {
  const { bookList } = useLoaderData<LoaderData>();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const { showTopButton } = useDisplayStickyButton();

  // Get filter directly from URL (single source of truth)
  const filterByType = searchParams.get("type");

  // Memoize filtered books to avoid recalculation
  const filteredBooks = useMemo(() => {
    if (!filterByType) return bookList;
    return bookList.filter((book) => book.type?.toLowerCase() === filterByType.toLowerCase());
  }, [filterByType, bookList]);

  const handleApplyFilter = (value: string | number) => {
    const valueStr = String(value);
    const allLabel = lang === LANG.english ? "All" : "બધા";

    // Handle "All" option - clear filter
    if (valueStr === allLabel) {
      setSearchParams(
        (prev) => {
          prev.delete("type");
          return prev;
        },
        { replace: true },
      );
      return;
    }

    // Find the matching filter data by checking both English and Gujarati names
    const filterItem = SadGranthFilterData.find((item) => item.enName === valueStr || item.guName === valueStr);

    if (!filterItem) return;

    // Store as lowercase
    const englishNameLower = filterItem.enName.toLowerCase();
    const currentFilterLower = filterByType?.toLowerCase() || "";

    // Toggle: if same filter is clicked, clear it
    if (currentFilterLower === englishNameLower) {
      setSearchParams(
        (prev) => {
          prev.delete("type");
          return prev;
        },
        { replace: true },
      );
    } else {
      setSearchParams(
        (prev) => {
          prev.set("type", englishNameLower);
          return prev;
        },
        { replace: true },
      );
    }
  };

  const FilterChip = () => {
    const allLabel = lang === LANG.english ? "All" : "બધા";
    const chipDataWithAll = [allLabel, ...SadGranthFilterData.map((item) => (lang === LANG.english ? item.enName : item.guName))];

    const selectedValueWithAll = !filterByType
      ? allLabel
      : lang === LANG.english
        ? (SadGranthFilterData.find((item) => item.enName?.toLowerCase() === filterByType?.toLowerCase())?.enName ?? "")
        : (SadGranthFilterData.find((item) => item.enName?.toLowerCase() === filterByType?.toLowerCase())?.guName ?? "");

    return <Chip chipData={chipDataWithAll} selectedValue={selectedValueWithAll} onClick={handleApplyFilter} />;
  };

  return (
    <div className="mb-5">
      <div className={showTopButton ? "filter-chip-animation" : "filter-chip-non-animation"}>
        <FilterChip />
      </div>

      <ClientOnly
        fallback={
          <div className="mobile_container mb-3 px-2">
            <div className="book-card">
              {filteredBooks?.map((book) => (
                <React.Fragment key={book.id}>
                  <MemoizedSadGranthComponent book={book} />
                </React.Fragment>
              ))}
            </div>
          </div>
        }
      >
        {filteredBooks?.length > 0 ? (
          <div className="mobile_container mb-3 px-2">
            <div className="book-card">
              {filteredBooks?.map((book) => (
                <React.Fragment key={book.id}>
                  <MemoizedSadGranthComponent book={book} />
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-[50vh]">
            <NodataFound text="No data found" />
          </div>
        )}
      </ClientOnly>

      {bookList?.length ? (
        <div className="text-center">
          {/* <NodataFound text={t("common.noDataFoundFor", { data: title })} /> */}
          <NoHasMoreText className="text-textSecondaryColor mb-5" text={t("common.seeAllData", { data: t("common.sad_granth") })} />
        </div>
      ) : null}
    </div>
  );
};

export default SadGranth;

const MemoizedSadGranthComponent = memo(({ book }: { book: BookListType }) => {
  const lang = useLanguageParams() as "en" | "gu";
  // const monthName = shortMonthName[book.month - 1];
  return (
    <DisplayBookCard
      key={book.id}
      name={book.name.replaceAll("-", " ")}
      url={{
        small: book?.img?.s?.url || FALLBACK_IMAGE_URL,
        medium: book?.img?.m?.url || FALLBACK_IMAGE_URL,
        large: book?.img?.l?.url || FALLBACK_IMAGE_URL,
        hd: book?.img?.hd?.url || FALLBACK_IMAGE_URL,
        original: book?.img?.og?.url || FALLBACK_IMAGE_URL,
      }}
      pdf_path={book.pdf_path}
      img_aspect={book.img_aspect}
      isBookPart={book.n_part}
      hasAudio={book.has_audio_book}
      navigatePath={book.n_part > 1 ? `/sad-granth/${book.url_slug}/part-1` : `/sad-granth/${book.url_slug}`}
      downLoadFineName={book.name}
      tag={book.tag}
      blur_hash={book.img?.bh}
      playlist_id={book.playlist_id || null}
    />
  );
});
